/**
 * Phira constants used by the per-note and per-frame code (W1-06 engine-notes-frame).
 * Every value is cited from the research documents (rendering.md R§, skin-and-assets.md S§, rpe-format.md F§);
 * nothing is copied from Phira's source.
 */

import type { RGBAf } from '../../util/types';

/** Half note width in world units at noteScale 1 (R§0, S§0): `0.13175016 * noteScale`. */
export const NOTE_WIDTH_RATIO_BASE = 0.13175016;

/** Non-hold fade after the hit time on cover lines (R§4.4): 0.16 s. */
export const FADEOUT_TIME = 0.16;

/** Hold tick interval at playback rate 1 (R§5.1): 0.15 s. */
export const HOLD_TICK_INTERVAL = 0.15;

/**
 * One Phira frame of the deterministic 60 Hz model. Phira emits the first hold tick on the first frame after the
 * head is judged (R§5.1), and the paint simulation runs at fixed 60 Hz (PaintStampBatch).
 */
export const FRAME_DT = 1 / 60;

/** Cover rule threshold (R§4.5 item 5): hidden when `coverBase <= -0.001`. */
export const COVER_THRESHOLD = -0.001;

/** RPE x half width: `positionX / 675` = world x (R§4.1). */
export const RPE_HALF_WIDTH = 675;

/** RPE height in px: `yOffset * 2 / 900` (R§4.1). */
export const RPE_HEIGHT = 900;

/** Control-curve key scale: distance in RPE-y px = height distance * 450 (R§2.4). */
export const CONTROL_KEY_SCALE = 450;

/** Incline divisor (R§3.8): `... * 450 / 360`. */
export const INCLINE_DIVISOR = 360;

/** Off-screen quad cull bound in world units (R§4.5 item 6; y uses +-1, not +-1/ar: Phira quirk). */
export const OFFSCREEN_BOUND = 1;

/** World points whose line-local y bounds the aggressive culling limit (R§4.5 item 7): (+-1.1, +-1). */
export const CULL_POINT_X = 1.1;
export const CULL_POINT_Y = 1;

/** Hit-effect sprite half side = hitFxScale * noteScale / 5 (R§5.2). */
export const HIT_FX_HALF_DIVISOR = 5;

/** Square particles (R§5.3): 4 per burst, half side hitFxScale * noteScale / 44 * (1 - U(0, 0.3)). */
export const PARTICLE_COUNT = 4;
export const PARTICLE_HALF_DIVISOR = 44;
export const PARTICLE_SIZE_JITTER = 0.3;
/** Initial speed 2.5 * noteScale * (1 - U(0, 0.1)) world units per second, decay v' = -6 v. */
export const PARTICLE_SPEED = 2.5;
export const PARTICLE_SPEED_JITTER = 0.1;
export const PARTICLE_DECAY = 6;

/** Note alpha is a u16 in RPE; >= 255 is opaque (F§11.1). */
export const NOTE_ALPHA_MAX = 255;

/** Paint brush alpha multiplier (R§3.7): a = max(alpha, 0) * 2.55, saturating at 1. */
export const PAINT_ALPHA_FACTOR = 2.55;

/** Bad judgement window (F§11.6, R§5.5): a judgement lies within +-0.22 s of the note time. */
export const LIMIT_BAD = 0.22;

/** Opaque white. */
export const WHITE: RGBAf = [1, 1, 1, 1];
